"use client";
/**
 * Client island: mobile carousel for related case studies.
 * Isolates the Embla/Carousel client-side logic so the parent page can stay
 * a server component.
 */
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { decodeHtmlEntitiesSSR } from '@/lib/utils';

interface RelatedStory {
  id: string;
  slug: string;
  title: string;
  company: string;
  challenge?: string;
  featured_image?: string;
  industry?: string;
  [key: string]: unknown;
}

interface Props {
  stories: RelatedStory[];
}

function StoryCard({ story }: { story: RelatedStory }) {
  return (
    <Card className="group overflow-hidden rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
      <div className="relative h-48 bg-gradient-to-br from-primary/10 to-primary/5">
        {story.featured_image && (
          <img
            src={story.featured_image}
            alt={story.company}
            className="w-full h-full object-cover"
          />
        )}
        <Badge variant="secondary" className="absolute bottom-3 left-3">
          {story.industry}
        </Badge>
      </div>
      <CardContent className="p-5">
        <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {story.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {story.challenge
            ? `${decodeHtmlEntitiesSSR(story.challenge).slice(0, 120)}...`
            : ''}
        </p>
        <Link
          to={`/customer-stories/${story.slug}`}
          className="inline-flex items-center text-sm font-medium text-primary hover:underline"
        >
          Read Full Story <ArrowRight className="ml-1.5 h-4 w-4" />
        </Link>
      </CardContent>
    </Card>
  );
}

export function RelatedStoriesCarousel({ stories }: Props) {
  return (
    <>
      {/* Mobile carousel */}
      <Carousel opts={{ align: 'start' }} className="md:hidden -mx-4 px-4">
        <CarouselContent className="-ml-3">
          {stories.map((story) => (
            <CarouselItem key={story.id} className="pl-3 basis-[85%]">
              <StoryCard story={story} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Desktop grid */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stories.map((story) => (
          <StoryCard key={story.id} story={story} />
        ))}
      </div>
    </>
  );
}
